import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { selectAdminToken } from '../../admin/sessionSlice';
import { setBackButtonUrl } from '../uiSlice';
import { apiUrl } from '../../utils/apiUrl';
import '../game/game.css';
import './feedback.css';

const S3 = process.env.REACT_APP_S3_PATH || '';

function s3Url(filename) {
  if (!filename) return '';
  return S3 + encodeURIComponent(filename).replace(/%2F/g, '/');
}

export const FEEDBACK_PERFECT = 'Perfect as is 👍';
export const FEEDBACK_SPICE = 'Add some spice 🌟';

const OPTIONS = [
  { id: 'perfect', label: FEEDBACK_PERFECT },
  { id: 'spice', label: FEEDBACK_SPICE },
];

export default function Feedback() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);
  const { data: themeData, status: themeStatus } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);

  const [isMobile, setIsMobile] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  /** Loaded from DB so repeat visits (e.g. back → leaderboard → feedback) show prior choice */
  const [feedbackStatusLoaded, setFeedbackStatusLoaded] = useState(false);
  const [hadSavedFeedback, setHadSavedFeedback] = useState(false);
  const isFromMobileApp =
    new URLSearchParams(location.search).get('fromMobileApp') === 'true';
  const leaderboardPath = isFromMobileApp
    ? '/leaderboard?fromMobileApp=true'
    : '/leaderboard';

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);

  /** Header Back + browser stack: point exit URL to org portal, not leaderboard / in-app stack */
  useEffect(() => {
    if (!user) return;
    const base =
      (process.env.REACT_APP_BASE_URL || '').trim().replace(/\/+$/, '') || window.location.origin;
    dispatch(setBackButtonUrl(user.backButtonRedirect || base));
  }, [dispatch, user]);

  useEffect(() => {
    if (!user) return;
    if (!adminToken) {
      setFeedbackStatusLoaded(true);
      return;
    }
    let cancelled = false;
    const labels = OPTIONS.map((o) => o.label);
    (async () => {
      try {
        const resp = await fetch(apiUrl('/spotGameStatus'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: user.userId }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        const raw = data.userFeedback;
        if (data.success && raw && labels.includes(String(raw).trim())) {
          const label = String(raw).trim();
          setSelected(label);
          setHadSavedFeedback(true);
        }
      } catch (e) {
        console.error('spotGameStatus (feedback)', e);
      } finally {
        if (!cancelled) setFeedbackStatusLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken]);

  if (!user) {
    navigate('/login', { replace: true });
    return null;
  }

  function appBaseUrl() {
    const b = (process.env.REACT_APP_BASE_URL || '').trim().replace(/\/+$/, '');
    return b || window.location.origin;
  }
  
  function basePlansUrl() {
    return `${appBaseUrl()}`;
  }

  const btnTextColor = themeData?.button_color || '#ffffff';
  const themeTextColorStyle = { ...(themeData?.text_color && { color: themeData.text_color }) };

  const sceneBg = isMobile
    ? (themeData?.background_mob ? `url(${s3Url(themeData.background_mob)})` : 'none')
    : (themeData?.background_desk ? `url(${s3Url(themeData.background_desk)})` : 'none');

  const boxBg = isMobile
    ? (themeData?.mobile_box ? `url(${s3Url(themeData.mobile_box)})` : 'none')
    : (themeData?.desktop_box ? `url(${s3Url(themeData.desktop_box)})` : 'none');

  const saveFeedback = async (label) => {
    if (!adminToken || submitting) return;
    setSubmitting(true);
    setSelected(label);
    try {
      const resp = await fetch(apiUrl('/saveUserFeedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: user.userId,
          userFeedback: label,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.success === false) {
        await Swal.fire({
          icon: 'error',
          title: 'Could not save',
          text: data.error || resp.statusText || 'Try again later',
        });
        setSelected(null);
        setSubmitting(false);
        return;
      }
      await Swal.fire({
        icon: 'success',
        title: 'Thanks!',
        text: 'Your feedback was saved.',
        timer: 2000,
        showConfirmButton: false,
      });
      const base =
        (process.env.REACT_APP_BASE_URL || '').trim().replace(/\/+$/, '') || window.location.origin;
      dispatch(setBackButtonUrl(user.backButtonRedirect || base));
      setHadSavedFeedback(true);
      navigate(leaderboardPath, {
        replace: true,
        state: { feedbackSubmitted: true },
      });
    } catch (e) {
      console.error('saveUserFeedback', e);
      await Swal.fire({ icon: 'error', title: 'Network error', text: String(e.message) });
      setSelected(null);
    }
    setSubmitting(false);
  };

  if (themeStatus !== 'succeeded' || !themeData || !feedbackStatusLoaded) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <div
      className={`tot-scene${isMobile ? ' is-mobile' : ''}`}
      style={{ backgroundImage: sceneBg }}
      aria-label="Feedback"
    >
      <h1 className="tot-title tot-feedback-title" style={themeTextColorStyle}>
        Was this a perfect mix or needs a remix? 😉
      </h1>

      {hadSavedFeedback && (
        <p className="tot-feedback-hint" style={themeTextColorStyle}>
          You already sent feedback for this session — tap an option to update it.
        </p>
      )}

      <section className="tot-choices tot-feedback-choices tot-anim-idle" aria-label="Feedback options">
        {OPTIONS.map((opt) => (
          <article
            key={opt.id}
            className={`tot-choice-card tot-feedback-card${boxBg !== 'none' ? '' : ' tot-card-placeholder'}${selected === opt.label ? ' tot-feedback-selected' : ''}${selected && selected !== opt.label ? ' tot-not-selected' : ''}`}
            style={boxBg !== 'none' ? { backgroundImage: boxBg } : undefined}
            onClick={() => !submitting && saveFeedback(opt.label)}
            role="button"
            tabIndex={0}
            aria-label={opt.label}
            onKeyDown={(e) => e.key === 'Enter' && !submitting && saveFeedback(opt.label)}
          >
            <span className="tot-feedback-option-label" style={themeTextColorStyle}>
              {opt.label}
            </span>
          </article>
        ))}
      </section>

      {submitting && (
        <div className="tot-feedback-saving" style={{ color: btnTextColor }}>
          {/* Saving… */}
        </div>
      )}
    </div>
  );
}
